import { fetchSkillPathData, fetchTopicLessonsData } from './dataApi';

const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}$/;
const CEFR_LEVEL_PATTERN = /^[abc][12]$/;
const CHAPTER_KEY_PATTERN = /^index([1-9]\d*)$/;

function normalizedSegment(value, pattern, label) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!pattern.test(normalized)) {
    throw new TypeError(`Invalid topic-lessons ${label}`);
  }
  return normalized;
}

export function normalizeTopicLanguage(value) {
  return normalizedSegment(value, LANGUAGE_CODE_PATTERN, 'language');
}

export function normalizeTopicLevel(value) {
  return normalizedSegment(value, CEFR_LEVEL_PATTERN, 'level');
}

export function topicLessonsFromIndex(index) {
  if (!index || typeof index !== 'object' || Array.isArray(index)) return [];

  return Object.entries(index)
    .map(([key, entry]) => {
      const match = CHAPTER_KEY_PATTERN.exec(key);
      if (!match || !entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return null;
      }

      const id = typeof entry.id === 'string' ? entry.id.trim() : '';
      const label = typeof entry.label === 'string' ? entry.label.trim() : '';
      const path = typeof entry.path === 'string' ? entry.path.trim() : '';
      if (!id || !label || !path) return null;

      const authoredType = typeof entry.type === 'string'
        ? entry.type.trim().toLowerCase()
        : '';
      return {
        id,
        label,
        path,
        type: authoredType || 'lesson',
        chapterNumber: Number(match[1]),
      };
    })
    .filter(Boolean)
    .sort((first, second) => first.chapterNumber - second.chapterNumber);
}

export async function loadTopicLessonsCatalog(language, level, options) {
  const normalizedLanguage = normalizeTopicLanguage(language);
  const normalizedLevel = normalizeTopicLevel(level);
  const index = await fetchTopicLessonsData(
    `${normalizedLanguage}/topic-lessons/${normalizedLevel}/catalog.json`,
    options,
  );
  return topicLessonsFromIndex(index);
}

export function topicLessonItems(document) {
  if (Array.isArray(document)) return document.filter((item) => item && typeof item === 'object');
  if (!document || typeof document !== 'object') return [];
  return Object.entries(document)
    .filter(([key, item]) => /^\d+$/.test(key) && item && typeof item === 'object')
    .sort(([first], [second]) => Number(first) - Number(second))
    .map(([, item]) => item);
}

// Recursively merges a sparse overlay array onto the same-index root array
// entries (no "id" to key by — e.g. a section's content[] blocks, a
// points.items[] list, or a table's rows[] — each row itself an array of
// cells, so this recurses into nested arrays too, not just nested objects).
// An overlay entry that's an empty object/array (or absent, when the
// overlay array is shorter than root's) means "nothing to translate at
// this position" and keeps the root entry entirely as authored — so a row
// with no translatable cells never needs to be reproduced in the overlay.
function mergeArrayByPosition(current, overlay) {
  return current.map((entry, i) => {
    const overlayEntry = overlay[i];
    if (overlayEntry === undefined || overlayEntry === null) return entry;
    if (Array.isArray(entry) && Array.isArray(overlayEntry)) {
      return mergeArrayByPosition(entry, overlayEntry);
    }
    if (entry && typeof entry === 'object' && !Array.isArray(entry)
      && overlayEntry && typeof overlayEntry === 'object' && !Array.isArray(overlayEntry)) {
      return applyNativeOverlay(entry, overlayEntry);
    }
    return overlayEntry;
  });
}

// Deep-merges a sparse native-language overlay (only the fields authored
// with "translate": true) onto a lesson item. Plain leaf values (strings)
// replace directly; arrays of {id, ...} objects (e.g. match exercise
// "left"/"right") merge by id so untranslated siblings and ordering are
// preserved; any other array (of plain strings, or of objects with no
// "id" — section content[], points.items[]) merges by position instead,
// see mergeArrayByPosition.
export function applyNativeOverlay(item, overlay) {
  if (!overlay || typeof overlay !== 'object') return item;
  const merged = { ...item };
  for (const [key, value] of Object.entries(overlay)) {
    const current = merged[key];
    if (value && typeof value === 'object' && !Array.isArray(value)
      && current && typeof current === 'object' && !Array.isArray(current)) {
      merged[key] = applyNativeOverlay(current, value);
    } else if (Array.isArray(value) && Array.isArray(current)
      && value.length > 0 && value.every((entry) => entry && typeof entry === 'object' && 'id' in entry)) {
      const byId = new Map(value.map((entry) => [String(entry.id), entry]));
      merged[key] = current.map((entry) => {
        if (!entry || typeof entry !== 'object') return entry;
        const overlayEntry = byId.get(String(entry.id));
        if (!overlayEntry) return entry;
        const { id: _unused, ...rest } = overlayEntry;
        return { ...entry, ...rest };
      });
    } else if (Array.isArray(value) && Array.isArray(current)) {
      merged[key] = mergeArrayByPosition(current, value);
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

// Best-effort fetch of the sparse native overlay for one lesson (see
// topic-lessons/{level}/native/{nativeCode}/{lessonFile}). Most
// lesson/language combinations don't have one yet, so a missing file is
// not an error — the target-language content is shown as-is.
async function loadNativeOverlay(language, level, chapterPath, nativeCode, options) {
  if (!nativeCode || nativeCode === language) return null;
  try {
    return await fetchTopicLessonsData(
      `${language}/topic-lessons/${level}/native/${nativeCode}/${chapterPath}`,
      options,
    );
  } catch {
    return null;
  }
}

// Alphabet lessons reuse the same index-catalog + numbered-item document
// shape as topic-lessons (see topicLessonsFromIndex/topicLessonItems above),
// but live at {home}/alphabet/ instead of {home}/topic-lessons/{level}/ —
// no CEFR level, and split across lesson1.json, lesson2.json, ... files a
// learner picks between rather than one continuous flow. Falls back to a
// single synthetic chapter pointing at lesson1.json for any language that
// doesn't have an alphabet/catalog.json yet, so it still opens directly
// instead of showing an empty picker.
export async function loadAlphabetCatalog(targetCode, defaultLabel) {
  const index = await fetchSkillPathData(targetCode, 'alphabet/catalog.json').catch(() => null);
  const chapters = topicLessonsFromIndex(index);
  if (chapters.length > 0) return chapters;
  return [{ id: 'alphabet', label: defaultLabel || 'Alphabet', path: 'lesson1.json', type: 'lesson', chapterNumber: 1 }];
}

// Best-effort fetch of the sparse native overlay for one alphabet lesson
// file (see alphabet/native/{nativeCode}/{chapterPath}) — mirrors
// loadNativeOverlay above. Most language/chapter/native-code combinations
// don't have one authored yet, so a missing file is not an error; the
// target-language content is shown as-is.
async function loadAlphabetNativeOverlay(targetCode, chapterPath, nativeCode, options) {
  if (!nativeCode || nativeCode === targetCode) return null;
  try {
    return await fetchSkillPathData(
      targetCode,
      `alphabet/native/${nativeCode}/${chapterPath}`,
      options,
    );
  } catch {
    return null;
  }
}

export async function loadAlphabetLesson(targetCode, chapterId, defaultLabel, nativeCode) {
  const chapters = await loadAlphabetCatalog(targetCode, defaultLabel);
  const chapter = chapters.find((item) => item.id === chapterId) || chapters[0];
  if (!chapter || !/^[a-z0-9/_-]+\.json$/i.test(chapter.path)) {
    throw new Error('Alphabet lesson not found');
  }
  const [document, native] = await Promise.all([
    fetchSkillPathData(targetCode, `alphabet/${chapter.path}`),
    loadAlphabetNativeOverlay(targetCode, chapter.path, nativeCode),
  ]);
  // "interactive": false marks a chapter as a static overview (e.g. an
  // intro/reference page) meant to render as one scrollable page instead of
  // the card-by-card swipeable flow — defaults to true (the swipeable flow)
  // for lessons authored without the flag.
  const interactive = document.lesson?.interactive !== false;
  const items = topicLessonItems(document.lesson).map((item, i) => {
    const key = String(i + 1);
    return native && native[key] ? applyNativeOverlay(item, native[key]) : item;
  });
  return { chapter, chapters, interactive, items };
}

export async function loadTopicLesson(language, level, lessonId, options) {
  const normalizedLanguage = normalizeTopicLanguage(language);
  const normalizedLevel = normalizeTopicLevel(level);
  const id = String(lessonId || '').trim();
  if (!id) throw new TypeError('Invalid topic-lessons lesson');

  const { nativeCode, ...fetchOptions } = options || {};
  const chapters = await loadTopicLessonsCatalog(normalizedLanguage, normalizedLevel, fetchOptions);
  const chapter = chapters.find((item) => item.id === id);
  if (!chapter || !/^[a-z0-9/_-]+\.json$/i.test(chapter.path)) {
    throw new Error('Topic lesson not found');
  }
  const [document, native] = await Promise.all([
    fetchTopicLessonsData(
      `${normalizedLanguage}/topic-lessons/${normalizedLevel}/${chapter.path}`,
      fetchOptions,
    ),
    loadNativeOverlay(normalizedLanguage, normalizedLevel, chapter.path, nativeCode, fetchOptions),
  ]);
  // Every chapter here is swipeable/interactive by nature (topic-lessons has
  // no static-overview concept the way an alphabet lesson1 does), so this
  // defaults true and isn't expected to ever be authored false — the flag
  // is read anyway so this module's document shape stays consistent with
  // alphabet/pronunciation-guide's lesson:{interactive, ...} convention.
  const interactive = document.lesson?.interactive !== false;
  const items = topicLessonItems(document.lesson).map((item, i) => {
    const key = String(i + 1);
    return native && native[key] ? applyNativeOverlay(item, native[key]) : item;
  });
  return { chapter, chapters, interactive, items };
}
